import * as Popover from '@radix-ui/react-popover';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentType, KeyboardEvent, MouseEvent, ReactNode, UIEvent } from 'react';

import { translate } from '../../common/helpers/translate.helper';
import { IconAlertTriangle } from '../../common/icons/cdl/AlertTriangle';
import { IconCornerDownLeft } from '../../common/icons/cdl/CornerDownLeft';
import { IconSearch } from '../../common/icons/cdl/Search';
import { IconSelector } from '../../common/icons/cdl/Selector';
import { IconX } from '../../common/icons/cdl/X';
import { InfoPopover } from '../../common/InfoPopover/InfoPopover';
import { Key } from '../../common/ShortcutMenu/components/Key';
import { Spinner } from '../../common/Spinner/Spinner';
import { Box } from '../../common/ui/Box';
import { Caption } from '../Caption/Caption';
import type { CaptionProps } from '../Caption/Caption';
import { Optional } from '../Input/components/Optional';
import { Required } from '../Input/components/Required';
import { Label } from '../Label/Label';
import { LabelProvider } from '../Label/LabelProvider';
import { Text } from '../Text/Text';

import { ActionContainer } from './components/ActionContainer';
import { ClearIconButton } from './components/ClearIconButton';
import { ComboBoxContainer } from './components/ComboBoxContainer';
import { Content } from './components/Content';
import { DropdownTriggerControl } from './components/DropdownTriggerControl';
import { HiddenTagMeasurement } from './components/HiddenTagMeasurement';
import { IconWrapper } from './components/IconWrapper';
import { OptionButton } from './components/OptionButton';
import { OptionCheck } from './components/OptionCheck';
import { OptionLabel } from './components/OptionLabel';
import { Placeholder } from './components/Placeholder';
import { SearchInput } from './components/SearchInput';
import { SearchSection } from './components/SearchSection';
import { Section } from './components/Section';
import { SelectedCount } from './components/SelectedCount';
import { SingleValue } from './components/SingleValue';
import { StateMessage } from './components/StateMessage';
import { ValueContainer } from './components/ValueContainer';
import { ValueTag } from './components/ValueTag';
import { ValueTagContainer } from './components/ValueTagContainer';
import { ValueTagLabel } from './components/ValueTagLabel';
import { ValueTagRemoveButton } from './components/ValueTagRemoveButton';
import { ValueTagRoot } from './components/ValueTagRoot';
import { useVisibleComboBoxTags } from './hooks/useVisibleComboBoxTags';
import { ComboBoxChangeAction, type ComboBoxChangeMeta } from './types/ComboBoxChangeMeta';
import type { ComboBoxOptionComponentProps, ComboBoxValueComponentProps } from './types/ComboBoxComponentProps';
import { ComboBoxMode } from './types/ComboBoxMode';
import type { ComboBoxOption } from './types/ComboBoxOption';
import { DEFAULT_MIN_WIDTH, FOCUS_RING_WIDTH, ComboBoxKeyboardKey } from './utils/ComboBoxConstants';
import { getSelectedOptions } from './utils/getSelectedOptions';

const COMBO_BOX_LOAD_MORE_THRESHOLD = 160;

interface ComboBoxBaseSharedProps<Option extends ComboBoxOption> {
    async: boolean;
    open: boolean;
    search: string;
    options?: Option[];
    label?: string;
    labelInfo?: string;
    placeholder?: string;
    searchPlaceholder?: string;
    caption?: ReactNode;
    CaptionIcon?: CaptionProps['Icon'];
    disabled?: boolean;
    positive?: boolean;
    negative?: boolean;
    markAsRequired?: boolean;
    markAsOptional?: boolean;
    description?: string;
    isClearable?: boolean;
    isSearchable?: boolean;
    minWidth?: string;
    dropdownWidth?: string;
    dropdownMaxHeight?: string;
    noOptionsMessage?: string;
    optionsErrorMessage?: string;
    selectedOptionsErrorMessage?: string;
    onBlur?: () => void;
    LeadingIcon?: ComponentType<{ width?: number; height?: number }>;
    selectedOptions?: Option[];
    onSelectionChange?: (selectedOptions: Option[], meta: ComboBoxChangeMeta<Option>) => void;
    onOpenChange: (open: boolean) => void;
    onSearchChange: (search: string) => void;
    customOptionsFilter?: (options: Option[], search: string) => Option[];
    onTriggerPointerEnter?: () => void;
    onOptionsScrollEnd?: () => void;
    isInitialLoading?: boolean;
    isSearchLoading?: boolean;
    isLoadingMoreOptions?: boolean;
    isSelectedOptionsLoading?: boolean;
    showSelectedOptionAtTop?: boolean;
    clearSearchOnSelect?: boolean;
    OptionComponent?: ComponentType<ComboBoxOptionComponentProps<Option>>;
    OptionSeparatorComponent?: ComponentType<ComboBoxOptionComponentProps<Option>>;
    ValueComponent?: ComponentType<ComboBoxValueComponentProps<Option>>;
}

interface ComboBoxBaseSingleProps<Option extends ComboBoxOption> extends ComboBoxBaseSharedProps<Option> {
    mode: ComboBoxMode.Single;
    value?: Option['value'] | null;
}

interface ComboBoxBaseMultiProps<Option extends ComboBoxOption> extends ComboBoxBaseSharedProps<Option> {
    mode: ComboBoxMode.Multi;
    value?: Array<Option['value']>;
}

export type ComboBoxBaseProps<Option extends ComboBoxOption> =
    | ComboBoxBaseSingleProps<Option>
    | ComboBoxBaseMultiProps<Option>;

export const ComboBoxBase = <Option extends ComboBoxOption = ComboBoxOption>({
    mode,
    async,
    open,
    search,
    value,
    onSelectionChange,
    onOpenChange,
    onSearchChange,
    options = [],
    selectedOptions: selectedOptionsProp = [],
    customOptionsFilter,
    onTriggerPointerEnter,
    onOptionsScrollEnd,
    isInitialLoading = false,
    isSearchLoading = false,
    isLoadingMoreOptions = false,
    isSelectedOptionsLoading = false,
    showSelectedOptionAtTop = true,
    clearSearchOnSelect = true,
    label,
    labelInfo,
    placeholder = '',
    searchPlaceholder = translate('comboBox.searchPlaceholder'),
    caption,
    CaptionIcon,
    disabled = false,
    positive = false,
    negative = false,
    markAsRequired = false,
    markAsOptional = false,
    description,
    isClearable = true,
    isSearchable = true,
    minWidth = DEFAULT_MIN_WIDTH,
    dropdownWidth,
    dropdownMaxHeight,
    noOptionsMessage = translate('comboBox.noOptions'),
    optionsErrorMessage,
    selectedOptionsErrorMessage,
    onBlur,
    LeadingIcon,
    OptionComponent,
    OptionSeparatorComponent,
    ValueComponent,
}: ComboBoxBaseProps<Option>) => {
    const isMulti = mode === ComboBoxMode.Multi;
    const searchInputRef = useRef<HTMLInputElement>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const optionsScrollRef = useRef<HTMLDivElement>(null);
    const lastScrollResetSearchRef = useRef('');
    const [searchInputHasFocus, setSearchInputHasFocus] = useState(false);
    const selectedOptions = useMemo(() => {
        const selectedValues = (() => {
            if (isMulti) {
                return value ?? [];
            }

            if (value === null || value === undefined) {
                return [];
            }

            return [value];
        })();

        return getSelectedOptions({ value: selectedValues, options, selectedOptions: selectedOptionsProp });
    }, [isMulti, options, selectedOptionsProp, value]);
    const hasSelectedOptionsError = !!selectedOptionsErrorMessage;
    const displaySelectedOptions = hasSelectedOptionsError ? [] : selectedOptions;
    const { measuredCountRef, measuredTagRefs, triggerTagsRef, triggerValueRef, visibleTagCount } =
        useVisibleComboBoxTags(displaySelectedOptions);
    const hasValue = displaySelectedOptions.length > 0;
    const visibleTagOptions = displaySelectedOptions.slice(
        Math.max(displaySelectedOptions.length - visibleTagCount, 0)
    );
    const hiddenTagCount = displaySelectedOptions.length - visibleTagOptions.length;

    const hasSearch = search.trim().length > 0;
    const selectedValueSet = useMemo(() => new Set(selectedOptions.map((option) => option.value)), [selectedOptions]);
    const { filteredOptions, searchableOptions } = useMemo(() => {
        const customFilteredOptions = customOptionsFilter ? customOptionsFilter(options, search) : options;
        const searchableOptions = async
            ? customFilteredOptions
            : customFilteredOptions.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));

        if (!isMulti && async && showSelectedOptionAtTop) {
            const selectedOption = selectedOptions[0];

            if (!selectedOption) {
                return { filteredOptions: searchableOptions, searchableOptions };
            }

            const selectedOptionIsSearchResult = searchableOptions.some(
                (option) => option.value === selectedOption.value
            );

            if (hasSearch && !selectedOptionIsSearchResult) {
                return { filteredOptions: searchableOptions, searchableOptions };
            }

            return {
                filteredOptions: [
                    selectedOption,
                    ...searchableOptions.filter((option) => option.value !== selectedOption.value),
                ],
                searchableOptions,
            };
        }

        if (!isMulti) {
            return { filteredOptions: searchableOptions, searchableOptions };
        }

        return {
            filteredOptions: searchableOptions.filter((option) => !selectedValueSet.has(option.value)),
            searchableOptions,
        };
    }, [
        async,
        isMulti,
        options,
        search,
        customOptionsFilter,
        hasSearch,
        selectedOptions,
        selectedValueSet,
        showSelectedOptionAtTop,
    ]);
    const enterSelectableOption =
        search &&
        !isSearchLoading &&
        !isInitialLoading &&
        filteredOptions.length === 1 &&
        !filteredOptions[0].disabled &&
        (isClearable || !selectedValueSet.has(filteredOptions[0].value))
            ? filteredOptions[0]
            : null;
    const activeEnterSelectableOption = searchInputHasFocus ? enterSelectableOption : null;
    const effectiveOptionsErrorMessage =
        optionsErrorMessage ?? (hasSelectedOptionsError ? translate('comboBox.couldNotLoadOptions') : undefined);
    const hasOnlySelectedOptions = isMulti && searchableOptions.length > 0 && filteredOptions.length === 0;
    const emptyOptionsMessage = (() => {
        if (hasSearch && hasOnlySelectedOptions) {
            return translate('comboBox.allMatchingOptionsAreAlreadySelected');
        }

        if (hasSearch) {
            return translate('comboBox.noOptionsMatchYourSearch');
        }

        if (hasOnlySelectedOptions) {
            return translate('comboBox.allOptionsAreAlreadySelected');
        }

        return noOptionsMessage;
    })();
    const effectiveCaption = selectedOptionsErrorMessage ?? caption;
    const EffectiveCaptionIcon = selectedOptionsErrorMessage ? IconAlertTriangle : CaptionIcon;
    const isCaptionNegative = negative || hasSelectedOptionsError;

    useEffect(() => {
        if (search.trim() === '') {
            lastScrollResetSearchRef.current = '';
            return;
        }

        if (!open || isInitialLoading || isSearchLoading || search === lastScrollResetSearchRef.current) {
            return;
        }

        if (optionsScrollRef.current) {
            optionsScrollRef.current.scrollTop = 0;
        }

        lastScrollResetSearchRef.current = search;
    }, [isInitialLoading, isSearchLoading, open, search]);

    const emitChange = (nextSelectedOptions: Option[], meta: ComboBoxChangeMeta<Option>) => {
        onSelectionChange?.(nextSelectedOptions, meta);
    };

    const removeOption = (
        option: Option,
        action: ComboBoxChangeMeta<Option>['action'] = ComboBoxChangeAction.Remove
    ) => {
        const nextOptions = selectedOptions.filter((selectedOption) => selectedOption.value !== option.value);
        emitChange(nextOptions, { action, option, options: nextOptions });
    };

    const clearValue = (event?: MouseEvent<HTMLElement>) => {
        event?.stopPropagation();
        emitChange([], { action: ComboBoxChangeAction.Clear, option: null, options: [] });
    };

    const clearSearchAndFocusInput = () => {
        onSearchChange('');
        requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });
    };

    const focusSearchInput = () => {
        requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });
    };

    const removeOptionAndFocusInput = (option: Option, action?: ComboBoxChangeMeta<Option>['action']) => {
        removeOption(option, action);
        clearSearchAndFocusInput();
    };

    const removeSelectedOptionAndFocusAdjacent = (option: Option) => {
        const removedOptionIndex = selectedOptions.findIndex((selectedOption) => selectedOption.value === option.value);

        removeOption(option);

        requestAnimationFrame(() => {
            const dropdownElement = searchInputRef.current?.closest('[data-combo-box-dropdown]');
            const selectedOptionButtons = Array.from(
                dropdownElement?.querySelectorAll<HTMLButtonElement>('[data-combo-box-selected-option]') ?? []
            ).filter((button) => !button.disabled);
            const nextFocusedIndex = Math.min(removedOptionIndex, selectedOptionButtons.length - 1);

            if (nextFocusedIndex >= 0) {
                selectedOptionButtons[nextFocusedIndex]?.focus();
                return;
            }

            searchInputRef.current?.focus();
        });
    };

    const focusSelectedOption = (option: Option) => {
        requestAnimationFrame(() => {
            const dropdownElement = searchInputRef.current?.closest('[data-combo-box-dropdown]');
            const selectedOptionButton = Array.from(
                dropdownElement?.querySelectorAll<HTMLButtonElement>('[data-combo-box-selected-option]') ?? []
            ).find((button) => button.dataset.value === option.value);

            selectedOptionButton?.focus();
        });
    };

    const closeAndFocusTrigger = () => {
        triggerRef.current?.focus({ preventScroll: true });
        onOpenChange(false);
    };

    const handleOptionClick = (option: Option) => {
        if (option.disabled) {
            return;
        }

        if (isMulti) {
            if (selectedValueSet.has(option.value)) {
                removeOptionAndFocusInput(option, ComboBoxChangeAction.Deselect);
                return;
            }

            const nextOptions = [...selectedOptions, option];
            emitChange(nextOptions, { action: ComboBoxChangeAction.Select, option, options: nextOptions });

            if (!async) {
                focusSelectedOption(option);
                return;
            }

            if (clearSearchOnSelect) {
                clearSearchAndFocusInput();
            } else {
                focusSearchInput();
            }
            return;
        }

        if (selectedValueSet.has(option.value)) {
            emitChange([], { action: ComboBoxChangeAction.Deselect, option, options: [] });
            return;
        }

        emitChange([option], { action: ComboBoxChangeAction.Select, option, options: [option] });
        onSearchChange('');
        closeAndFocusTrigger();
    };

    const handleOpenChange = (nextOpen: boolean) => {
        onOpenChange(nextOpen);

        if (!nextOpen) {
            onSearchChange('');
            onBlur?.();
        }
    };

    const handleTagRemoveClick = (
        event: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>,
        option: Option
    ) => {
        event.stopPropagation();
        removeOption(option);
    };

    const getDropdownElement = (element: HTMLElement) =>
        element.closest('[data-combo-box-dropdown]') ?? document.querySelector('[data-combo-box-dropdown]');

    const getEnabledOptionButtons = (element: HTMLElement) => {
        const dropdownElement = getDropdownElement(element);

        return Array.from(
            dropdownElement?.querySelectorAll<HTMLButtonElement>(
                '[data-combo-box-selected-option], [data-combo-box-option]'
            ) ?? []
        ).filter((button) => !button.disabled);
    };

    const focusOptionByArrowKey = (event: KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
        if (event.key !== ComboBoxKeyboardKey.ArrowDown && event.key !== ComboBoxKeyboardKey.ArrowUp) {
            return false;
        }

        const optionButtons = getEnabledOptionButtons(event.currentTarget);

        if (optionButtons.length === 0) {
            return false;
        }

        event.preventDefault();
        event.stopPropagation();

        if (event.currentTarget === searchInputRef.current) {
            const nextButton =
                event.key === ComboBoxKeyboardKey.ArrowDown
                    ? optionButtons[0]
                    : optionButtons[optionButtons.length - 1];
            nextButton.focus();
            return true;
        }

        const currentIndex = optionButtons.indexOf(event.currentTarget as HTMLButtonElement);

        if (event.key === ComboBoxKeyboardKey.ArrowDown) {
            if (currentIndex >= optionButtons.length - 1) {
                searchInputRef.current?.focus();
                return true;
            }

            optionButtons[currentIndex + 1]?.focus();
            return true;
        }

        if (currentIndex <= 0) {
            searchInputRef.current?.focus();
            return true;
        }

        optionButtons[currentIndex - 1]?.focus();
        return true;
    };

    const handleSelectedOptionKeyDown = (event: KeyboardEvent<HTMLButtonElement>, option: Option) => {
        if (event.key === ComboBoxKeyboardKey.Backspace) {
            event.preventDefault();
            event.stopPropagation();
            removeSelectedOptionAndFocusAdjacent(option);
            return;
        }

        focusOptionByArrowKey(event);
    };

    const handleOptionKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        focusOptionByArrowKey(event);
    };

    const handleTriggerBlur = () => {
        if (!open) {
            onBlur?.();
        }
    };

    const handleTriggerClickCapture = (event: MouseEvent<HTMLDivElement>) => {
        if (!disabled) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
    };

    const handleTriggerKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (disabled || event.target !== event.currentTarget) {
            return;
        }

        if (event.key === ComboBoxKeyboardKey.Enter || event.key === ComboBoxKeyboardKey.Space) {
            event.preventDefault();
            handleOpenChange(!open);
            return;
        }

        if (event.key === ComboBoxKeyboardKey.Backspace && hasValue && isMulti) {
            const lastDisplayedOption = displaySelectedOptions[displaySelectedOptions.length - 1];

            if (lastDisplayedOption) {
                removeOption(lastDisplayedOption);
            }
            return;
        }
    };

    const handleSearchInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (focusOptionByArrowKey(event)) {
            return;
        }

        if (event.key === ComboBoxKeyboardKey.Tab && !event.shiftKey) {
            const dropdownElement = getDropdownElement(event.currentTarget);
            const optionButtons = Array.from(
                dropdownElement?.querySelectorAll<HTMLButtonElement>('[data-combo-box-option]') ?? []
            );
            const optionButton = optionButtons.find((button) => {
                if (!button || button.disabled) {
                    return false;
                }

                const value = button.dataset.value;
                return !!value && !selectedValueSet.has(value as Option['value']);
            });

            let nextFocusedButton: HTMLButtonElement | null | undefined = null;

            if (optionButton) {
                nextFocusedButton = optionButton;
            }

            if (nextFocusedButton) {
                event.preventDefault();
                event.stopPropagation();
                nextFocusedButton.focus();
            }

            return;
        }

        if (event.key !== ComboBoxKeyboardKey.Enter || !enterSelectableOption) {
            return;
        }

        event.preventDefault();
        handleOptionClick(enterSelectableOption);
    };

    const handleOptionsScroll = (event: UIEvent<HTMLDivElement>) => {
        if (!onOptionsScrollEnd || isLoadingMoreOptions) {
            return;
        }

        const scrollElement = event.currentTarget;
        const distanceToBottom = scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight;

        if (distanceToBottom <= COMBO_BOX_LOAD_MORE_THRESHOLD) {
            onOptionsScrollEnd();
        }
    };

    const renderTriggerValue = () => {
        if (isSelectedOptionsLoading) {
            return (
                <Box
                    data-testid="combo-box-selected-options-loading"
                    alignItems="center"
                    display="flex"
                    gap={6}
                    minWidth={0}
                >
                    <Spinner size={16} />
                    <Text variant="small" color="foreground.subtle">
                        {translate('global.loading')}
                    </Text>
                </Box>
            );
        }

        if (hasValue && !isMulti) {
            if (ValueComponent) {
                return (
                    <SingleValue label={selectedOptions[0].label}>
                        <ValueComponent option={selectedOptions[0]} />
                    </SingleValue>
                );
            }

            return <SingleValue label={selectedOptions[0].label}>{selectedOptions[0].label}</SingleValue>;
        }

        if (hasValue) {
            return (
                <>
                    {hiddenTagCount > 0 ? (
                        <SelectedCount
                            variant="extraSmall"
                            fontWeight={600}
                            color={disabled ? 'foreground.disabled' : 'foreground.default'}
                        >
                            {hiddenTagCount}
                        </SelectedCount>
                    ) : null}
                    <ValueTagContainer ref={triggerTagsRef}>
                        {visibleTagOptions.map((option) => (
                            <ValueTag
                                key={option.value}
                                option={option}
                                onRemove={disabled ? undefined : handleTagRemoveClick}
                            />
                        ))}
                    </ValueTagContainer>
                </>
            );
        }

        return (
            <Placeholder variant="small" color="foreground.subtle">
                {placeholder}
            </Placeholder>
        );
    };

    return (
        <LabelProvider>
            <ComboBoxContainer $minWidth={minWidth}>
                {label ? (
                    <Box display="flex" alignItems="center" gap={4}>
                        <Label aria-disabled={disabled}>
                            {label}
                            {markAsRequired ? <Required disabled={disabled} /> : null}
                            {markAsOptional ? <Optional disabled={disabled} /> : null}
                        </Label>
                        {labelInfo ? (
                            <InfoPopover>
                                <Text variant="extraSmall">{labelInfo}</Text>
                            </InfoPopover>
                        ) : null}
                    </Box>
                ) : null}

                {description ? (
                    <Text variant="extraSmall" color="foreground.muted">
                        {description}
                    </Text>
                ) : null}

                <Popover.Root open={open} onOpenChange={handleOpenChange}>
                    <Popover.Trigger asChild>
                        <DropdownTriggerControl
                            ref={triggerRef}
                            data-testid="combo-box-trigger"
                            role="button"
                            tabIndex={disabled ? -1 : 0}
                            aria-disabled={disabled}
                            $isOpen={open}
                            $disabled={disabled}
                            $positive={positive}
                            $negative={negative}
                            $minWidth={minWidth}
                            $isMulti={isMulti}
                            onBlur={handleTriggerBlur}
                            onClickCapture={handleTriggerClickCapture}
                            onKeyDown={handleTriggerKeyDown}
                            onPointerEnter={disabled ? undefined : onTriggerPointerEnter}
                        >
                            {LeadingIcon ? <LeadingIcon width={16} height={16} /> : null}
                            <ValueContainer ref={triggerValueRef}>{renderTriggerValue()}</ValueContainer>
                            <ActionContainer $disabled={disabled}>
                                {isClearable && hasValue ? (
                                    <ClearIconButton
                                        data-testid="combo-box-clear"
                                        type="button"
                                        Icon={IconX}
                                        emphasis="transparent"
                                        disabled={disabled}
                                        onClick={disabled ? undefined : clearValue}
                                    />
                                ) : null}
                                {!hasValue || !isClearable ? (
                                    <IconWrapper flex="none">
                                        <IconSelector width={16} height={16} />
                                    </IconWrapper>
                                ) : null}
                            </ActionContainer>
                        </DropdownTriggerControl>
                    </Popover.Trigger>

                    <Popover.Portal>
                        <Content
                            data-testid="combo-box-dropdown"
                            data-combo-box-dropdown
                            align="start"
                            side="bottom"
                            sideOffset={FOCUS_RING_WIDTH}
                            avoidCollisions={false}
                            collisionPadding={8}
                            $isSearchable={isSearchable}
                            $maxHeight={dropdownMaxHeight}
                            $minWidth={minWidth}
                            $width={dropdownWidth ?? 'var(--radix-popover-trigger-width)'}
                        >
                            {isSearchable ? (
                                <SearchSection $disabled={hasSelectedOptionsError}>
                                    <IconSearch width={16} height={16} />
                                    <SearchInput
                                        ref={searchInputRef}
                                        placeholder={searchPlaceholder}
                                        value={search}
                                        disabled={hasSelectedOptionsError}
                                        onChange={(event) => onSearchChange(event.target.value)}
                                        onFocus={() => setSearchInputHasFocus(true)}
                                        onBlur={() => setSearchInputHasFocus(false)}
                                        onKeyDownCapture={handleSearchInputKeyDown}
                                    />
                                    {isSearchLoading ? (
                                        <Box
                                            data-testid="combo-box-search-loading"
                                            alignItems="center"
                                            display="flex"
                                            flex="none"
                                            height="24px"
                                            justifyContent="center"
                                            width="24px"
                                        >
                                            <Spinner size={14} />
                                        </Box>
                                    ) : null}
                                </SearchSection>
                            ) : null}

                            <Box
                                ref={optionsScrollRef}
                                data-testid="combo-box-options-scroll"
                                flex={1}
                                minHeight={0}
                                overflowY="auto"
                                style={{ overscrollBehavior: 'contain' }}
                                onScroll={handleOptionsScroll}
                            >
                                {isMulti && !hasSelectedOptionsError && selectedOptions.length > 0 ? (
                                    <Section $separated>
                                        {selectedOptions.map((option) => (
                                            <OptionButton
                                                key={option.value}
                                                type="button"
                                                tabIndex={-1}
                                                data-combo-box-selected-option
                                                data-value={option.value}
                                                $selected
                                                $hasOverride={!!OptionComponent}
                                                onClick={() => removeSelectedOptionAndFocusAdjacent(option)}
                                                onKeyDown={(event) => handleSelectedOptionKeyDown(event, option)}
                                            >
                                                {OptionComponent ? (
                                                    <OptionComponent
                                                        option={option}
                                                        selected
                                                        disabled={!!option.disabled}
                                                        search={search}
                                                    />
                                                ) : (
                                                    <OptionLabel
                                                        variant="small"
                                                        fontWeight={500}
                                                        $selected
                                                        label={option.label}
                                                    />
                                                )}
                                                <OptionCheck width={16} height={16} />
                                            </OptionButton>
                                        ))}
                                    </Section>
                                ) : null}

                                <Section>
                                    {isInitialLoading ? (
                                        <Box
                                            data-testid="combo-box-options-loading"
                                            alignItems="center"
                                            display="flex"
                                            justifyContent="center"
                                            minHeight="96px"
                                        >
                                            <Spinner />
                                        </Box>
                                    ) : null}
                                    {!isInitialLoading &&
                                    !effectiveOptionsErrorMessage &&
                                    filteredOptions.length === 0 ? (
                                        <StateMessage>{emptyOptionsMessage}</StateMessage>
                                    ) : null}
                                    {!isInitialLoading && effectiveOptionsErrorMessage ? (
                                        <StateMessage>{effectiveOptionsErrorMessage}</StateMessage>
                                    ) : null}
                                    {!isInitialLoading && !effectiveOptionsErrorMessage && !hasSelectedOptionsError
                                        ? filteredOptions.map((option) => {
                                              const selected = selectedValueSet.has(option.value);
                                              const optionComponentProps = {
                                                  option,
                                                  selected,
                                                  disabled: !!option.disabled,
                                                  search,
                                              };

                                              return (
                                                  <Fragment key={option.value}>
                                                      {OptionSeparatorComponent ? (
                                                          <OptionSeparatorComponent {...optionComponentProps} />
                                                      ) : null}
                                                      <OptionButton
                                                          type="button"
                                                          data-combo-box-option
                                                          data-value={option.value}
                                                          data-selected={selected}
                                                          $selected={selected}
                                                          $hasOverride={!!OptionComponent}
                                                          $highlighted={
                                                              activeEnterSelectableOption?.value === option.value
                                                          }
                                                          disabled={option.disabled}
                                                          onClick={() => handleOptionClick(option)}
                                                          onKeyDown={handleOptionKeyDown}
                                                      >
                                                          {OptionComponent ? (
                                                              <OptionComponent {...optionComponentProps} />
                                                          ) : (
                                                              <OptionLabel
                                                                  variant="small"
                                                                  fontWeight={500}
                                                                  $selected={!isMulti && selected}
                                                                  label={option.label}
                                                              />
                                                          )}
                                                          {selected ? (
                                                              <OptionCheck width={16} height={16} />
                                                          ) : activeEnterSelectableOption?.value === option.value ? (
                                                              <Box
                                                                  data-testid="combo-box-enter-hint"
                                                                  alignItems="center"
                                                                  display="flex"
                                                                  justifySelf="end"
                                                                  gap={8}
                                                              >
                                                                  <Key Icon={IconCornerDownLeft} />
                                                                  <Text
                                                                      variant="extraSmall"
                                                                      color="foreground.subtle"
                                                                      style={{ whiteSpace: 'nowrap' }}
                                                                  >
                                                                      {translate('comboBox.toSelect')}
                                                                  </Text>
                                                              </Box>
                                                          ) : null}
                                                      </OptionButton>
                                                  </Fragment>
                                              );
                                          })
                                        : null}
                                    {isLoadingMoreOptions ? (
                                        <StateMessage data-testid="combo-box-options-loading-more">
                                            <Box display="flex" justifyContent="center" width="100%">
                                                <Spinner size={14} />
                                            </Box>
                                        </StateMessage>
                                    ) : null}
                                </Section>
                            </Box>
                        </Content>
                    </Popover.Portal>
                </Popover.Root>

                <HiddenTagMeasurement>
                    <div ref={measuredCountRef}>
                        <SelectedCount
                            variant="extraSmall"
                            fontWeight={600}
                            color={disabled ? 'foreground.disabled' : 'foreground.default'}
                        >
                            {displaySelectedOptions.length}
                        </SelectedCount>
                    </div>
                    {displaySelectedOptions.map((option, index) => (
                        <div
                            key={option.value}
                            ref={(node) => {
                                measuredTagRefs.current[index] = node;
                            }}
                        >
                            <ValueTagRoot>
                                <ValueTagLabel label={option.label} showTitle={false} />
                                <ValueTagRemoveButton as="span">
                                    <IconX width={12} height={12} />
                                </ValueTagRemoveButton>
                            </ValueTagRoot>
                        </div>
                    ))}
                </HiddenTagMeasurement>

                {effectiveCaption ? (
                    <Caption $negative={isCaptionNegative} Icon={EffectiveCaptionIcon}>
                        {effectiveCaption}
                    </Caption>
                ) : null}
            </ComboBoxContainer>
        </LabelProvider>
    );
};
